import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { MessageService } from 'primeng/api';

import { Observable, Subject, Subscription, catchError, combineLatest, exhaustMap, tap, throwError, switchMap, take, of } from 'rxjs';

import { EmailAccountConnectorConfigApi } from '@api/emailAccountConnector/models/email-account-connector-config.api';
import { EmailAccountConnectorApi } from '@api/emailAccountConnector/models/email-account-connector.api';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { allowedFileTypes } from '../../../Common/Forms/models/file-upload.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { validateEmail } from '../../../Common/Misc/helpers/formatting/email.helper';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { DataConnectorModel } from '../../../Core/Users/models/data-connector.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DataConnectionService } from '../../../Core/Users/services/data-connection.service';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';
import { DataConnectorTypesEnum } from '../../../api/externalData/enums/data-connector-types.enum';
import { DataConnectorApiModel } from '../../../api/externalData/models/data-connector-api.model';
import { connectionTypeToIcon, connectionTypeToLocalizationKey } from '../DataConnector/data-connector.component';
import { EmailAccountConnectorComponent, NylasConnectionResult, NylasConnectionResultType } from '../EmailAccountConnector/email-account-connector.component';
import { gateCall } from '@Core/rxjs-utils';

interface UsedEmailAccountConnectors {
    sharedConnectors: number;
    privateConnectors: number;
}

@Component({
    selector: 'agpr-new-data-connection-dialog',
    styleUrls: ['./new-data-connection-dialog.component.scss'],
    templateUrl: './new-data-connection-dialog.component.html',
    standalone: false
})
export class NewDataConnectionDialogComponent extends AbstractDialogComponent implements OnDestroy {

    @Input() id: string;
    @Input() helpLink: string = 'https://help.agilitypr.com/en/articles/5301399-how-to-connect-google-analytics-to-your-agility-account';

    @Input() selectedConnectionType: DataConnectorTypesEnum = DataConnectorTypesEnum.GOOGLE_ANALYTICS_4;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    readonly buttonTypes = ButtonTypesEnum;
    readonly dataConnectorTypes = DataConnectorTypesEnum;
    connectionTypes = [];
    readonly connectionTypeToLocalizationKey = connectionTypeToLocalizationKey;
    readonly connectionTypeToIcon = connectionTypeToIcon;
    readonly messageSeverityEnum = messageSeverityEnum;

    readonly jsonMIMETypesRegex: string = allowedFileTypes.jsonMIMETypesRegex;
    readonly jsonMIMETypes = [
        ...allowedFileTypes.jsonMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ].join(',');

    connection: DataConnectorModel = new DataConnectorModel();

    connectionName: string;
    uploadedFiles = [];
    email: string;
    numOfRemaining: number = 0;
    emailAccountConnectorConfig: EmailAccountConnectorConfigApi;
    numOfUsedEmailAccountConnectors: UsedEmailAccountConnectors = {
        privateConnectors: 0,
        sharedConnectors: 0
    };


    privacyDropdownItems = [
        new DropdownOptionModel('DataConnector.emailAccount.newConnection.privacy.shared', EmailAccountConnectorPrivacyLevelEnum.SHARED),
        new DropdownOptionModel('DataConnector.emailAccount.newConnection.privacy.private', EmailAccountConnectorPrivacyLevelEnum.PRIVATE)
    ];

    selectedPrivacy = this.privacyDropdownItems[0];

    connectionErrorList = [
        'COM-001',
        'COM-002',
        'COM-003',
    ];

    errors: any = {
        wrongEmailFormat: false,
        hasNoEmail: false,
        hasNoName: false,
        duplicateEmail: false
    };

    isFormDataValid = true;
    connectorPermissionsLoading = false;

    subscriptions: Subscription[] = [];

    private createEmailAccountConnectorSubject = new Subject<{
        emailAccountConnector: EmailAccountConnectorApi;
        code: string;
        state: string;
    }>();

    constructor(
        coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        protected configService: ConfigService,
        private dataConnectionService: DataConnectionService,
        private currentUserStateService: CurrentUserStateService,
        private activeRoute: ActivatedRoute,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        super(coreDialogService, 'NewDataConnection');
        this.subscriptions.push(
            gateCall(this.dataConnectionService.currentUserHasEmailAccountConnectorAccessOnEnvironment()).pipe(
                switchMap(() => combineLatest([
                    this.dataConnectionService.emailAccountConfig$,
                    this.dataConnectionService.emailAccountConnectors$
                ]))
            ).subscribe(([config, emailAccountConnectors]) => {
                this.emailAccountConnectorConfig = config;
                this.numOfUsedEmailAccountConnectors.sharedConnectors = 0;
                this.numOfUsedEmailAccountConnectors.privateConnectors = 0;

                emailAccountConnectors.forEach(item => {
                    if (item.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED) {
                        this.numOfUsedEmailAccountConnectors.sharedConnectors ++;
                    } else {
                        this.numOfUsedEmailAccountConnectors.privateConnectors ++;
                    }
                });

                this.calculateRemaining();
            }),
            combineLatest([
                this.currentUserStateService.getPermissions(),
                this.dataConnectionService.hasEmailAccountProvision$,
                this.currentUserStateService.getAccount()
            ]).subscribe(([permissions, hasEmailAccountProvision, account]) => {
                // TODO: Remove when 3PP released
                const useEmailAccountConnectors = hasEmailAccountProvision && this.configService.accountHasAccessToThirdPartyPitchingOnEnvironment(account.id);
                this.connectionTypes = [];

                // TODO Unhide when social connections are available
                // if (permissions.includes(userPermissionsEnum.socialIntelligence)) {
                //     this.connectionTypes.push(...[
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.TWITTER_ANALYTICS],
                //             value: DataConnectorTypesEnum.TWITTER_ANALYTICS,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.TWITTER_ANALYTICS]
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.FACEBOOK_ANALYTICS],
                //             value: DataConnectorTypesEnum.FACEBOOK_ANALYTICS,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.FACEBOOK_ANALYTICS]
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.INSTAGRAM_ANALYTICS],
                //             value: DataConnectorTypesEnum.INSTAGRAM_ANALYTICS,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.INSTAGRAM_ANALYTICS]
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.YOUTUBE_ANALYTICS],
                //             value: DataConnectorTypesEnum.YOUTUBE_ANALYTICS,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.YOUTUBE_ANALYTICS]
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.TIKTOK_ANALYTICS],
                //             value: DataConnectorTypesEnum.TIKTOK_ANALYTICS,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.TIKTOK_ANALYTICS]
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.REDDIT_ANALYTICS],
                //             value: DataConnectorTypesEnum.REDDIT_ANALYTICS,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.REDDIT_ANALYTICS]
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.LINKEDIN],
                //             value: DataConnectorTypesEnum.LINKEDIN,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.LINKEDIN] @TODO missing iconUrl
                //         },
                //         {
                //             label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.BLUESKY],
                //             value: DataConnectorTypesEnum.BLUESKY,
                //             iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.BLUESKY] @TODO missing iconUrl
                //         },
                //     ]);
                // }

                if (permissions.includes(userPermissionsEnum.advancedMonitoring)) {
                    this.connectionTypes.push(...[
                        {
                            label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.GOOGLE_ANALYTICS_4],
                            value: DataConnectorTypesEnum.GOOGLE_ANALYTICS_4,
                            iconUrl: connectionTypeToIcon[DataConnectorTypesEnum.GOOGLE_ANALYTICS_4]
                        },
                    ]);
                }

                if (permissions.includes(userPermissionsEnum.advancedTargeting) && useEmailAccountConnectors) {
                    this.connectionTypes.push(
                        {
                            label: connectionTypeToLocalizationKey[DataConnectorTypesEnum.EMAIL_ACCOUNT],
                            value: DataConnectorTypesEnum.EMAIL_ACCOUNT,
                            icon: 'fa-regular fa-duotone fa-envelope',
                        }
                    );
                }
            }),
            this.createEmailAccountConnectorSubject.asObservable().pipe(
                exhaustMap(data => this.dataConnectionService.createEmailAccountConnector(data.emailAccountConnector, data.code, data.state).pipe(
                    tap(id => {
                        this.messageService.add({
                            severity: messageSeverityEnum.success,
                            summary: 'DataConnector.newDataConnection',
                            detail: this.localizationService.transform('DataConnector.successfullyCreated', { name: data.emailAccountConnector.connectorName })
                        });
                        this.connectorPermissionsLoading = false;
                    }),
                    catchError(error => {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'DataConnector.dataConnectionFailed',
                            detail: (this.connectionErrorList.find(err => err === error?.error?.details[0]?.code)) ?
                                this.localizationService.transform('DataConnector.error.' + error?.error?.details[0]?.code) :
                                this.localizationService.transform(
                                    'DataConnector.connectionCouldNotBeEstablished',
                                    { name: data.emailAccountConnector.connectorName }
                                )
                        });
                        return throwError(() => new Error(error));
                    })
                ))
            ).subscribe({
                next: id => {
                    this.closeDialog(true);
                },
                error: () => {
                    this.closeDialog(false);
                }
            })
        );
    }

    validateFormData(event?: any): boolean {
        this.isFormDataValid = true;
        if (!this.selectedConnectionType) {
            this.isFormDataValid = false;
        }
        if (!this.connectionName || this.connectionName.trim().length === 0) {
            this.isFormDataValid = false;
        }

        if (this.selectedConnectionType === DataConnectorTypesEnum.EMAIL_ACCOUNT) {
            if (this.errors.wrongEmailFormat || this.errors.hasNoEmail || this.errors.duplicateEmail) {
                this.isFormDataValid = false;
            }

            if (this.numOfRemaining <= 0) {
                this.isFormDataValid = false;
            }
        } else {
            if (!this.uploadedFiles || this.uploadedFiles.length !== 1) {
                this.isFormDataValid = false;
            }
        }

        return this.isFormDataValid;
    }

    addDataConnection(): void {
        if (!this.validateFormData()) {
            return;
        }

        if (this.selectedConnectionType === DataConnectorTypesEnum.EMAIL_ACCOUNT) {
            const emailAccountConnector = {
                connectorName: this.connectionName,
                connectorEmail: this.email,
                privacyLevel: this.selectedPrivacy.value
            } as EmailAccountConnectorApi;


            this.dataConnectionService.buildRedirectUrl(emailAccountConnector)
                .pipe(catchError(error => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'DataConnector.dataConnectionFailed',
                        detail: (this.connectionErrorList.find(err => err === error?.error?.details[0]?.code)) ?
                            this.localizationService.transform('DataConnector.error.' + error?.error?.details[0]?.code) :
                            this.localizationService.transform('DataConnector.connectionCouldNotBeEstablished', { name: emailAccountConnector.connectorName })
                    });

                    return of('');
                }))
                .subscribe((redirectUri) => {
                    if (!redirectUri) {
                        this.connectorPermissionsLoading = false;
                        throw new Error('Could not retrieve nylas url');
                    }

                    const channel = new BroadcastChannel(EmailAccountConnectorComponent.MESSAGE_ID);
                    const listener = e => {
                        if (e.origin !== window.location.origin || e.data.key !== EmailAccountConnectorComponent.MESSAGE_ID) {
                            return;
                        }

                        channel.removeEventListener('message', listener);
                        channel.close();

                        const result: NylasConnectionResult = e.data.result;
                        if (result.resultType === NylasConnectionResultType.authError) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'DataConnector.dataConnectionFailed',
                                detail: this.localizationService.transform(
                                    'DataConnector.connectionCouldNotBeEstablished',
                                    { name: emailAccountConnector.connectorName }
                                )
                            });
                            this.closeDialog(false);
                        } else {
                            this.createEmailAccountConnectorSubject.next({
                                emailAccountConnector: emailAccountConnector,
                                code: result.code,
                                state: result.state
                            });
                        }
                    };
                    channel.addEventListener ('message', listener);
                    this.connectorPermissionsLoading = true;
                    window.open(redirectUri);
                });
        } else {
            const apiModel: DataConnectorApiModel = {
                type: this.selectedConnectionType,
                name: this.connectionName
            };

            this.dataConnectionService.createDataConnector(apiModel, this.uploadedFiles[0].file).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'DataConnector.newDataConnection',
                        detail: this.localizationService.transform('DataConnector.successfullyCreated', { name: apiModel.name })
                    });
                    this.closeDialog(true);
                },
                error: (error) => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'DataConnector.dataConnectionFailed',
                        detail: (this.connectionErrorList.find(err => err === error?.error?.details[0]?.code)) ?
                            this.localizationService.transform('DataConnector.error.' + error?.error?.details[0]?.code) :
                            this.localizationService.transform('DataConnector.connectionCouldNotBeEstablished', { name: apiModel.name })
                    });
                    this.closeDialog(false);
                }
            });
        }

    }

    validateEmail(): void {
        this.dataConnectionService.emailAccountConnectors$.pipe(take(1)).subscribe(emailConnectors => {
            if (this.email) {
                this.errors.wrongEmailFormat = !validateEmail(this.email);
            } else {
                this.errors.wrongEmailFormat = false;
            }
            this.errors.hasNoEmail = !this.email?.trim();

            if (this.selectedConnectionType === DataConnectorTypesEnum.EMAIL_ACCOUNT) {
                this.errors.duplicateEmail = emailConnectors.some(emailConnector => emailConnector.connectorEmail === this.email?.trim());
            } else {
                this.errors.duplicateEmail = false;
            }

            this.validateFormData();
        });

    }

    cancel(): void {
        this.closeDialog(false);
    }

    getMoreConnections(): void {
        if (this.configService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
        } else {
            window.open('https://www.agilitypr.com/contact/');
        }
    }

    calculateRemaining(): void {
        if (this.selectedPrivacy.value === EmailAccountConnectorPrivacyLevelEnum.SHARED) {
            this.numOfRemaining = this.emailAccountConnectorConfig.sharedConnectorLimit - this.numOfUsedEmailAccountConnectors.sharedConnectors;
        } else {
            this.numOfRemaining = this.emailAccountConnectorConfig.privateConnectorLimit - this.numOfUsedEmailAccountConnectors.privateConnectors;
        }

        this.validateFormData();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
